/**
 * `AIInterfaceModel.ts` — port of Unity's `AI/UI/AIInterfaceModel.cs`.
 *
 * UI-facing Spark facade: mirrors session events for panels, owns interface
 * preferences (debug mode, constant listening, minimal panel mode — stored
 * under Unity's `Campfire.AI.*` pref keys in localStorage), and the
 * push-to-talk presentation state. Session lifecycle stays in
 * `AIRealtimeSessionModel`; UI must not reach past this facade.
 */

import { Emitter } from "@core/events/emitter";
import type { AIRealtimeSessionModel, RealtimeSessionStatus } from "@ai/orchestrator/AIRealtimeSessionModel";
import type { RealtimeMicrophoneReason } from "@ai/orchestrator/IRealtimeTransport";
import type { AssistantText } from "@ai/orchestrator/RealtimeSession";
import type { AIConversationPresentationModel } from "./AIConversationPresentationModel";
import type { IAIObservabilityModel } from "@ai/observability/AIObservabilityModel";
import type { IRealtimeConversationLog } from "@ai/observability/RealtimeConversationLog";
import type {
  AIRealtimeRoleDefinition,
  AIRealtimeSkillDefinition,
} from "@ai/guardrails/aiRealtimeContentTypes";
import { loadAvailableSkillsAsync } from "@ai/guardrails/AIRealtimeSkillCatalog";
import { loadAvailableRolesAsync } from "@ai/guardrails/AIRealtimeRoleCatalog";
import type { IAIPromptStoreModel } from "@ai/guardrails/IAIPromptStoreModel";
import { ensureBasePromptLoadedAsync } from "@ai/guardrails/AIRealtimeBasePromptCatalog";
import { ensurePersonalitiesLoadedAsync } from "@ai/guardrails/AIRealtimePersonalityCatalog";
import { DEFAULT_ROLE_ID, setRoleDefinitions } from "@ai/guardrails/agentPrompts";
import {
  buildAvailableSkillsIndexSection,
  buildLoadedSkillsPromptSection,
} from "@ai/guardrails/skillPromptSections";
import { AgentToolNames } from "@ai/registry/agentToolNames";
import type { IAppSettingsModel } from "@models/settings/IAppSettingsModel";

const PREF_DEBUG_MODE = "Campfire.AI.DebugMode";
const PREF_CONSTANT_LISTENING = "Campfire.AI.ConstantListeningEnabled";
const PREF_MINIMAL_PANEL = "Campfire.AI.ShowPanelOnlyForResponsesAndCitations";

/**
 * Push-to-talk release is a hard audio boundary. Both transports preserve
 * already-captured audio and continue supplying silence for server VAD, so no
 * post-release microphone window is required. Retained in the log payload so
 * a trace states the active policy rather than requiring a source lookup.
 */
const K_PTT_RELEASE_TAIL_MS = 0;

function readPref(key: string, fallback: boolean): boolean {
  try {
    const value = localStorage.getItem(key);
    return value === null ? fallback : value === "true";
  } catch {
    return fallback;
  }
}

function writePref(key: string, value: boolean): void {
  try {
    localStorage.setItem(key, String(value));
  } catch {
    // Storage unavailable (private mode) — prefs stay session-only.
  }
}

// Meta/read tools that must stay available regardless of the active role's tool restriction, so the
// agent can always reason, fetch context, switch roles, load skills, and drive its own presentation.
// Role/skill `tools:` lists only need to declare action tools on top of these. Mirrors Unity
// `AIInterfaceModel.s_coreAlwaysAllowedToolIds` (ids kept even for tools the web hasn't registered
// yet — an allow-list entry for an unregistered tool is inert).
//
// **The PROJECT LIFECYCLE is deliberately not here** (`Get_Available_Projects`, `Open_Project`,
// `create_new_project`, `exit_project`), unlike Unity's set: opening, creating and leaving a project
// are ACTIONS, and a role has to be able to not have them. The Guide narrates a training run and
// must not be able to walk the learner out of the project mid-run — a rule the role's prose cannot
// enforce on its own. The roles that own the lifecycle declare it in their `tools:` instead
// (`assistant` all four; `workflow-builder` just `exit_project`), and the role with no project
// open is always the default one, so nothing loses a reachable capability.
// The shared `/AI/roles/*.md` stay valid for Unity too, where those ids are already core and a
// duplicate declaration is a no-op.
//
// **This is the SHIPPED set, not the effective one.** The deployment's settings document can add an
// id to it or withdraw one (`AppSettingsDto.coreTools`, written from the debug panel's Tools tab), so
// everything that asks "is this tool core" must go through `AIInterfaceModel.isToolCore` /
// `coreToolIds`. Reading this set directly answers what the BUILD ships and silently ignores the
// deployment's decision.
export const K_CORE_ALWAYS_ALLOWED_TOOL_IDS: ReadonlySet<string> = new Set(
  [
    AgentToolNames.SwitchRole,
    AgentToolNames.LoadSkill,
    AgentToolNames.PresentOptions,
    AgentToolNames.WaitFor,
    AgentToolNames.DoNotWait,
    AgentToolNames.ResetConversation,
    AgentToolNames.Undo,
    AgentToolNames.ToggleSpark,
    AgentToolNames.ToggleAIPanel,
    AgentToolNames.ToggleTranscript,
    AgentToolNames.ToggleGuide,
    AgentToolNames.ToggleDebugMode,
    AgentToolNames.ChangePersonality,
    AgentToolNames.UseConstantListening,
    AgentToolNames.ChangeWakeGesture,
    AgentToolNames.ListenUser,
    AgentToolNames.SendUserText,
    AgentToolNames.StandHere,
    AgentToolNames.StandWhereverYouWant,
    AgentToolNames.TakeScreenshot,
    AgentToolNames.CreateMdFile,
    AgentToolNames.GetProjectContext,
    AgentToolNames.GetSceneContext,
    AgentToolNames.GetPartContext,
    AgentToolNames.PromptSceneContextAgent,
  ].map((id) => id.toLowerCase()),
);

/** Outcome of handing a picked file to the agent. */
export type AIFileInputResult = { ok: true } | { ok: false; reason: string };

/**
 * File-input seam, registered by the orchestrator for the same reason as
 * {@link AIRoleController}: AI UI must not import `ai/orchestrator/*`.
 *
 * It exists because whether a file can be sent AS A FILE depends on which
 * transport owns the active role's turns — the Responses bridge has the API's
 * `input_file` / `input_image` parts, the realtime protocol has neither. The
 * panel asks; it does not work that out for itself.
 */
export interface AIFileInputController {
  /** True while the active role's turns run on a transport with real file inputs. */
  supportsFileInput(): boolean;
  /**
   * The file picker's `accept` list for this transport. Supplied by the
   * controller so the panel never has to import the transport's own format
   * tables out of `ai/orchestrator/*`.
   */
  readonly accept: string;
  /**
   * Uploads + sends the file as one user turn, carrying `text` when the user
   * typed a message alongside it. Empty `text` is the bare-attachment case the
   * bridge already words for itself.
   */
  sendFileAsync(file: File, text: string): Promise<AIFileInputResult>;
}

/**
 * Role-switch seam the orchestrator registers into the interface model so AI UI can switch the
 * agent role without importing `ai/orchestrator/*` (Unity's panel calls the orchestrator directly).
 *
 * One method, because there is only one kind of switch: a role is session state, so the "@" picker
 * and the role gate withdrawing an active role both want the same `setAgentRole`.
 */
export interface AIRoleController {
  switchRole(roleId: string): void;
}

/**
 * What the Spark panel's CONVERSATION is doing — the chat the user reads, which
 * they also call the transcript.
 *
 * Three independent facts rather than one boolean, because "it is not on
 * screen" has three different causes and the agent has to say a different thing
 * for each: there is nothing to show yet, something else is suppressing it, or
 * it is simply collapsed. The wording is the executor's; this is the state.
 */
export interface AITranscriptState {
  /**
   * There is at least one RENDERED message (or a pending `present_options`
   * round). Read off the list the panel actually draws, not the raw history: a
   * guided run drops a script's spoken lines, so a conversation of nothing but
   * those has nothing to show and expanding it would open an empty box over the
   * scene.
   */
  readonly hasMessages: boolean;
  /** The messages are on screen right now. */
  readonly isExpanded: boolean;
  /**
   * Something OTHER than the collapse is keeping them off screen — today only
   * `Toggle_AIPanel`'s minimal-panel mode, which shows the conversation for
   * citations and option rounds and hides it otherwise. Expanding cannot beat
   * it, so the agent has to say so rather than trying again.
   */
  readonly isSuppressed: boolean;
}

/**
 * The Spark panel's conversation controls, handed to the interface model so the
 * agent can press them — `Toggle_Transcript`'s whole implementation.
 *
 * The registered-host pattern, in the same direction as
 * {@link AIRoleController} and `IWorkflowMediaPlaybackHost`: the panel owns the
 * drawer's height and the host-body collapse flag, which a headless model must
 * never touch, and the model owns being reachable from the executor.
 *
 * ⚠ A returned disposer rather than `set…(host | null)`, for the reason
 * `attachMediaPlaybackHost` records: `AiPanel` is `v-if`'d by the shell — a
 * guided run drops it on every step that withholds `ai_input`, and the v2 and
 * split-bottom layouts swap which copy is mounted — and nothing orders the
 * outgoing teardown after the incoming setup. A bare `set(null)` on dispose
 * would wipe the registration that had just replaced it, and the failure is
 * silent: every command answers "no panel" until the panel remounts.
 */
export interface AITranscriptController {
  getTranscriptState(): AITranscriptState;
  /**
   * Show or hide the conversation, and answer where it ended up.
   *
   * Returns the SETTLED intent rather than a live read: showing it is a
   * coalesced re-fit a tick later, so a read straight afterwards would still
   * report it collapsed. The two ways a show can fail — nothing to show, and
   * suppressed — are both knowable synchronously, so the answer is exact
   * anyway.
   */
  setTranscriptExpanded(expanded: boolean): AITranscriptState;
}

/** The one role that can never be gated out — see `AIInterfaceModel.isRoleEnabled`. */
function isDefaultRoleId(roleId: string): boolean {
  return roleId?.trim().toLowerCase() === DEFAULT_ROLE_ID;
}

/** Order-independent identity of a tool-id set, for the change diffs below. */
function toolIdSignature(ids: Iterable<string>): string {
  return [...ids].sort().join("|");
}

export class AIInterfaceModel {
  // Session-mirrored events.
  readonly onStatusChanged = new Emitter<RealtimeSessionStatus>();
  /**
   * Assistant text, each piece labelled `answer` or `script` (a workflow step's
   * own narration) by the response that produced it — see `AssistantText`.
   * The label is the one source every consumer reads, so the panel and the
   * saved transcript cannot disagree about a line.
   */
  readonly onResponseReceived = new Emitter<AssistantText>();
  readonly onAssistantTextCompleted = new Emitter<AssistantText>();
  readonly onThinkingStateChanged = new Emitter<boolean>();
  /** A non-empty text turn explicitly submitted through the user-facing AI facade. */
  readonly onUserTextSubmitted = new Emitter<string>();
  readonly onUserVoiceInputStarted = new Emitter<void>();
  readonly onUserVoiceInputStopped = new Emitter<void>();
  readonly onUserVoiceTranscriptDelta = new Emitter<string>();
  readonly onUserVoiceTranscriptReceived = new Emitter<string>();
  readonly onToolExecutionStarted = new Emitter<{ toolName: string }>();
  readonly onToolExecutionCompleted = new Emitter<{ toolName: string; result: string }>();
  readonly onErrorOccurred = new Emitter<string>();
  /**
   * The agent's reasoning summary, streamed while it thinks, then settled with
   * the wall-clock the panel reports as "Thought for …". Only the Responses
   * text bridge ever publishes these — a realtime role simply never fires them,
   * so the panel needs no branch of its own.
   */
  readonly onReasoningStarted = new Emitter<void>();
  readonly onReasoningDelta = new Emitter<string>();
  readonly onReasoningCompleted = new Emitter<number>();
  readonly onUsageSummaryChanged = new Emitter<void>();

  // Interface-owned events.
  readonly onDebugModeChanged = new Emitter<boolean>();
  readonly onConstantListeningChanged = new Emitter<boolean>();
  readonly onPushToTalkActiveChanged = new Emitter<boolean>();
  readonly onMinimalPanelModeChanged = new Emitter<boolean>();
  readonly onActivityStateChanged = new Emitter<boolean>();
  /**
   * The two halves of `onActivityStateChanged`, re-emitted separately.
   *
   * That flag is the OR of thinking / tool-running / speaking / an open
   * response, which is the right grain for "Spark is busy" (the 3D crown) and
   * the wrong one for a caller that has to tell TALKING from WORKING — the
   * panel's activity ring already drops the speaking half, and `AiPanel`'s
   * mobile mic hold needs exactly that half on its own. Re-emitted here rather
   * than reached for on the session, per the harness rule that a caller outside
   * the session layer goes through this model.
   */
  readonly onAgentSpeakingChanged = new Emitter<boolean>();
  /** The microphone opened or closed — see `AIRealtimeSessionModel.onMutedChanged`. */
  readonly onMutedChanged = new Emitter<boolean>();
  /** Raised whenever available/loaded skill state changes (Unity `SkillsChanged`). */
  readonly onSkillsChanged = new Emitter<void>();

  readonly conversationPresentation: AIConversationPresentationModel;

  private readonly m_session: AIRealtimeSessionModel;
  private readonly m_observability: IAIObservabilityModel;
  private readonly m_log: IRealtimeConversationLog;
  private readonly m_getToolLabel: (toolName: string) => string | null;
  private readonly m_prompts: IAIPromptStoreModel;
  private readonly m_appSettings: IAppSettingsModel;

  private m_isDebugMode = readPref(PREF_DEBUG_MODE, false);
  private m_isConstantListening = readPref(PREF_CONSTANT_LISTENING, false);
  private m_minimalPanelMode = readPref(PREF_MINIMAL_PANEL, false);
  private m_isPushToTalkActive = false;
  /**
   * Whether the LIVE push-to-talk window may be closed by server VAD.
   *
   * True only when the mic was armed HANDS-FREE (`Listen_User`), where VAD is
   * the only thing that can end the turn. False for the Space gesture, whose
   * window belongs to the user finger -- see `onUserVoiceStopped`.
   *
   * Its one reader is that guard, and `m_isPushToTalkActive`s only writer is
   * the same line that writes this -- so arming always rewrites it and a stale
   * value can never coexist with an armed window. Clearing it on DISARM is
   * therefore defensive (it is what the field means: the window is over, so its
   * property is), and a negative control that skips the clear cannot bite.
   */
  private m_isPushToTalkAutoRelease = false;
  private m_isToolExecuting = false;
  /**
   * The microphone is under the user's MANUAL control right now, so nothing may
   * open it on their behalf (user direction 2026-09-04: "during workflow
   * playback on mobile - disable all kinds of automatic listening").
   *
   * Set by the phone's Spark panel for the length of a guided run, where the
   * mic is armed by a tap on the composer and by nothing else. Session-only and
   * deliberately NOT a preference: it describes what a surface is currently
   * doing, not something the user chose once.
   *
   * It is an interface-layer fact — "how is voice input being driven" sits
   * beside push-to-talk and constant listening — so the shell tells this model
   * rather than the tool reaching for the shell.
   */
  private m_isVoiceInputManual = false;

  // Progressive-disclosure skill/role state (Unity `AIInterfaceModel` skill region).
  /**
   * Every skill the manifest declares, gate or no gate — the CATALOG. Only the
   * settings UI reads this (it has to list a disabled skill to offer the switch
   * that re-enables it); nothing agent-facing may.
   */
  private readonly m_allSkills: AIRealtimeSkillDefinition[] = [];
  /**
   * The subset the account's settings leave ENABLED. Everything agent-facing
   * reads this — the prompt index, `Load_Skill`, the role tool gate — so a
   * disabled skill is not hidden from the model, it does not exist for it.
   */
  private readonly m_availableSkills: AIRealtimeSkillDefinition[] = [];
  private readonly m_loadedSkillIds: string[] = [];
  private m_loadedSkills: AIRealtimeSkillDefinition[] = [];
  /**
   * The ids in the ACTIVE role's scope as of the last time anything was applied
   * — see {@link applyRoleSkillScope}, which diffs against it. Empty is the
   * honest starting value: before any catalog loads there is nothing in scope.
   */
  private m_activeRoleScopeSignature = "";
  /**
   * The EFFECTIVE core tool set as of the last time anything was applied — see
   * {@link applyCoreToolSet}, which diffs against it. Seeded with the SHIPPED set
   * because that is exactly what this model serves until the settings document
   * lands, so a load carrying no core-tool decision reports no change rather than
   * a spurious session refresh at boot.
   */
  private m_coreToolSignature = toolIdSignature(K_CORE_ALWAYS_ALLOWED_TOOL_IDS);
  /** Every role the manifest declares — the CATALOG. Same split as the skills above. */
  private readonly m_roleDefinitions: AIRealtimeRoleDefinition[] = [];
  /**
   * The subset the account leaves ENABLED, always including the default role.
   * This is what reaches `setRoleDefinitions`, so a disabled role is absent from
   * `switch_role`'s enum and description, from the persona map, and from the "@"
   * picker — the agent is not asked to avoid it, it has no way to name it.
   */
  private readonly m_availableRoles: AIRealtimeRoleDefinition[] = [];
  private m_activeRoleId: string | null = null;
  private m_skillsLoadTask: Promise<boolean> | null = null;
  private m_hasLoadedSkillCatalog = false;
  private m_rolesLoadTask: Promise<boolean> | null = null;
  private m_hasLoadedRoleCatalog = false;
  private m_contentReloadTask: Promise<void> | null = null;
  private m_contentReloadQueued = false;
  /** Whether the CURRENT catalogs were built from any developer override. */
  private m_contentUsesPublished = false;
  /** Last-seen `IAIPromptStoreModel.publishedSignature`; see the subscription. */
  private m_publishedSignature = "";
  // The orchestrator registers a role-switch controller here (Unity: the panel calls the
  // orchestrator directly; the web keeps AI UI off `ai/orchestrator/*` by registering this
  // callback from the orchestrator, like `setRoleSkillToolFilterProvider`).
  private m_roleController: AIRoleController | null = null;
  // Registered by the orchestrator alongside the role controller — see
  // `AIFileInputController` for why the panel cannot answer this itself.
  private m_fileInputController: AIFileInputController | null = null;
  /** The Spark panel's conversation controls (see {@link AITranscriptController}). */
  private m_transcriptController: AITranscriptController | null = null;

  constructor(
    session: AIRealtimeSessionModel,
    presentation: AIConversationPresentationModel,
    observability: IAIObservabilityModel,
    getToolLabel: (toolName: string) => string | null,
    prompts: IAIPromptStoreModel,
    appSettings: IAppSettingsModel,
    log: IRealtimeConversationLog,
  ) {
    this.m_session = session;
    this.conversationPresentation = presentation;
    this.m_observability = observability;
    this.m_log = log;
    this.m_getToolLabel = getToolLabel;
    this.m_prompts = prompts;
    this.m_appSettings = appSettings;

    // The account's skill/role gate changing — an admin flipping a switch, or the
    // shared settings document simply finishing its first load — re-filters both
    // catalogs. No re-FETCH: which skills and roles exist and what they say has
    // not changed, only which of them the agent may be told about.
    appSettings.onChanged.on(() => {
      const rolesChanged = this.applyRoleGate();
      const skillsChanged = this.applySkillGate();
      // AFTER the skill gate, deliberately: the scope is derived from
      // `m_availableSkills`, so asking first would compare against the previous
      // enabled set and could miss — or invent — a change.
      const scopeChanged = this.applyRoleSkillScope();
      const coreChanged = this.applyCoreToolSet();
      if (!rolesChanged && !skillsChanged && !scopeChanged && !coreChanged) return;
      this.onSkillsChanged.emit();
      // A skill disabled while loaded was just pruned out of the prompt, one
      // re-enabled has to appear in the index, and a withdrawn role has to leave
      // `switch_role`; either way the live session is holding a stale list until
      // this lands. `leaveDisabledActiveRole` may have refreshed already — the
      // session coalesces, so asking twice is cheaper than working out which.
      this.m_session.refreshSessionContext();
    });

    // A published prompt changing has to re-read the catalogs it affects.
    //
    // The guard is deliberately two-sided. "Is anything published?" alone would
    // skip the one case that matters most: resetting the LAST published file
    // leaves nothing stored, so the edited prompt would stay live with no way
    // back to the shipped one. `m_contentUsesPublished` remembers whether the
    // CURRENT catalog was built from any, so "there is something to load" and
    // "there is something to unload" are both covered — while a deployment
    // running stock prompts still does not re-fetch 20 files on an idle event.
    prompts.onChanged.on(() => {
      // `onChanged` also fires for load settles — including the first one at
      // boot — so without this the boot pass would be followed immediately by a
      // redundant full re-fetch of every catalog whenever anything is published.
      const signature = prompts.publishedSignature;
      if (signature === this.m_publishedSignature) return;
      this.m_publishedSignature = signature;

      const publishesCatalogContent = prompts
        .listPublished()
        // The workflow schema is read on demand by its executor; changing it
        // does not alter any prompt catalog or the session.update payload.
        .some((record) => record.kind !== "workflow");
      if (!publishesCatalogContent && !this.m_contentUsesPublished) return;
      void this.requestContentReloadAsync().catch((e) =>
        console.warn("[AIInterfaceModel] Published-prompt reload failed:", e),
      );
    });

    session.onStatusChanged.on((s) => this.onStatusChanged.emit(s));
    session.onResponseDelta.on((d) => {
      this.m_observability.noteAssistantResponseDelta();
      this.onResponseReceived.emit(d);
    });
    session.onAssistantTextCompleted.on((t) => {
      this.m_observability.trackAssistantMessage(this.m_session.sessionId, t.text);
      this.onAssistantTextCompleted.emit(t);
    });
    session.onThinkingStateChanged.on((t) => {
      this.onThinkingStateChanged.emit(t);
      this.emitActivityState();
    });
    session.onUserVoiceStarted.on(() => this.onUserVoiceInputStarted.emit());
    session.onUserVoiceStopped.on(() => {
      // Wake-gesture PTT auto-release parity (`HandleRealtimeVoiceInputStopped`).
      //
      // ⚠ HANDS-FREE ARMING ONLY. Upstream fires this for a WAKE GESTURE, where
      // VAD is the only thing that CAN end the turn -- and `Listen_User` is the
      // web equivalent. The Space gesture is not: there the user finger owns
      // the window, and a mid-sentence pause of `silence_duration_ms` (500 ms)
      // would otherwise mute the mic WHILE THE KEY IS STILL PHYSICALLY DOWN.
      // Nothing re-opens it -- `AiPanel.onKeyDown` swallows every repeat while
      // `spaceHeld`, and the eventual keyup early-returns in
      // `setPushToTalkActive` because the flag is already false -- so the rest
      // of the sentence is captured by nothing and the user is given no reason.
      if (
        this.m_isPushToTalkAutoRelease &&
        this.m_isPushToTalkActive &&
        !this.m_isConstantListening
      ) {
        this.setPushToTalkActive(false);
      }
      this.onUserVoiceInputStopped.emit();
    });
    session.onUserTranscriptDelta.on((d) => this.onUserVoiceTranscriptDelta.emit(d));
    session.onUserTranscriptCompleted.on((t) => this.onUserVoiceTranscriptReceived.emit(t));
    session.onToolExecutionStarted.on((e) => {
      this.m_isToolExecuting = true;
      this.onToolExecutionStarted.emit(e);
      this.emitActivityState();
    });
    session.onToolExecutionCompleted.on((e) => {
      this.m_isToolExecuting = false;
      this.onToolExecutionCompleted.emit(e);
      this.emitActivityState();
    });
    // Unity `UpdateActivityState` re-evaluates on response lifecycle and
    // agent-speaking transitions too — without these the panel's activity
    // visualizer would stay latched after a response finished.
    session.onActiveResponseChanged.on(() => this.emitActivityState());
    session.onAgentSpeakingChanged.on((speaking) => {
      this.onAgentSpeakingChanged.emit(speaking);
      this.emitActivityState();
    });
    session.onMutedChanged.on((muted) => this.onMutedChanged.emit(muted));
    session.onErrorOccurred.on((code) => this.onErrorOccurred.emit(code));
    session.onUsageChanged.on(() => this.onUsageSummaryChanged.emit());
    session.onReasoningStarted.on(() => this.onReasoningStarted.emit());
    session.onReasoningDelta.on((d) => this.onReasoningDelta.emit(d));
    session.onReasoningCompleted.on((ms) => this.onReasoningCompleted.emit(ms));
  }

  // -- State -----------------------------------------------------------

  get status(): RealtimeSessionStatus {
    return this.m_session.status;
  }

  get hasSession(): boolean {
    return this.m_session.hasSession;
  }

  get isMuted(): boolean {
    return this.m_session.isMuted;
  }

  get usePushToTalk(): boolean {
    return !this.m_isConstantListening;
  }

  get isConstantListeningEnabled(): boolean {
    return this.m_isConstantListening;
  }

  get isVoiceInputManual(): boolean {
    return this.m_isVoiceInputManual;
  }

  get isPushToTalkActive(): boolean {
    return this.m_isPushToTalkActive;
  }

  get isThinking(): boolean {
    return this.m_session.isThinking;
  }

  get isAgentSpeaking(): boolean {
    return this.m_session.isAgentSpeaking;
  }

  get micInputLevel(): number {
    return this.m_session.micInputLevel;
  }

  get hasActiveResponse(): boolean {
    return this.m_session.hasActiveResponse;
  }

  get isActivityActive(): boolean {
    return (
      this.isThinking ||
      this.m_isToolExecuting ||
      this.isAgentSpeaking ||
      this.hasActiveResponse
    );
  }

  get isDebugMode(): boolean {
    return this.m_isDebugMode;
  }

  get showPanelOnlyForResponsesAndCitations(): boolean {
    return this.m_minimalPanelMode;
  }

  get usageSummaryText(): string {
    return this.m_session.usageTracker.summaryText;
  }

  get usageBreakdownText(): string {
    return this.m_session.usageTracker.breakdownText;
  }

  getToolLabelWhenActive(toolName: string): string | null {
    return this.m_getToolLabel(toolName);
  }

  // -- Debug visibility -------------------------------------------------
  // `Campfire_AI_Harness.md` names debug visibility as one of the things a
  // caller outside the session layer should reach through a dedicated
  // interface/observability API for, rather than chaining through
  // `IAIOrchestratorModel.RealtimeSessionModel`. These two are that API, for
  // the "Context" tab of the developer debug panel.

  /**
   * The live `session.update` payload — instructions (which carry the project
   * and scene context), the role/skill-gated tool schemas, and the audio
   * config. Rebuilt on every call, so the panel always shows what would go on
   * the wire NOW rather than a snapshot from connect time. Answers while Spark
   * is offline too, which is when it is most worth reading.
   */
  buildDebugSessionPayload(): Record<string, unknown> {
    return this.m_session.buildSessionPayload();
  }

  /**
   * The per-turn `User Context:` message the next user turn would carry
   * (Unity `BuildTurnContext`) — camera pose, selection, recent selection. It
   * is a SEPARATE conversation item from the session payload above, so the
   * panel shows it as its own field rather than implying it is part of the
   * instructions.
   */
  buildDebugTurnContext(): string | null {
    return this.m_session.buildTurnUserContext();
  }

  // -- Commands ---------------------------------------------------------

  sendUserText(text: string): void {
    const trimmed = text.trim();
    if (!trimmed) return;
    this.m_session.resumeAudioOutputIfNeeded();
    this.onUserTextSubmitted.emit(trimmed);
    this.m_session.sendUserText(trimmed, "text");
  }

  /**
   * `caption` is the message the user typed alongside the picture, and it rides
   * the SAME conversation item — see `RealtimeSession.sendUserImage` for why it
   * cannot be a separate `sendUserText`.
   *
   * ⚠ Unlike `sendUserText` this does NOT emit `onUserTextSubmitted`, so a
   * captioned image does not count as a user turn for `wait_for user_response`,
   * does not clear the agent's scene visuals, and is not recorded in the
   * transcript. That is pre-existing for every attachment path and is left
   * alone here: the guided run that `wait_for` belongs to hides the attachment
   * button outright, so the combination is unreachable.
   */
  sendUserImage(dataUrl: string, caption?: string): boolean {
    return this.m_session.sendUserImage(dataUrl, caption);
  }

  /** The orchestrator registers its file-input handler here. */
  attachFileInputController(controller: AIFileInputController): void {
    this.m_fileInputController = controller;
  }

  /**
   * Whether the active role can take a file AS A FILE (PDF, spreadsheet, deck,
   * document, image …) rather than as pasted text. False on the realtime
   * transport, whose protocol has no file content part — the panel falls back
   * to its text/image handling there.
   */
  get supportsFileInput(): boolean {
    return this.m_fileInputController?.supportsFileInput() ?? false;
  }

  /**
   * The file picker's `accept` list while rich file input is live, else null
   * (the panel then falls back to its realtime text/image list).
   */
  get fileInputAccept(): string | null {
    const controller = this.m_fileInputController;
    return controller?.supportsFileInput() ? controller.accept : null;
  }

  /**
   * Sends a picked file as ONE user turn through the Responses file-input path,
   * carrying whatever the user typed with it (`text`, possibly empty).
   *
   * ⚠ Like `sendUserImage`, this does not emit `onUserTextSubmitted` — see the
   * note there for why the turn accounting is left as it was.
   */
  sendUserFileAsync(file: File, text = ""): Promise<AIFileInputResult> {
    const controller = this.m_fileInputController;
    if (!controller || !controller.supportsFileInput()) {
      return Promise.resolve({ ok: false, reason: "Files are not supported for this role." });
    }
    this.m_session.resumeAudioOutputIfNeeded();
    return controller.sendFileAsync(file, text);
  }

  setMuted(
    muted: boolean,
    reason: RealtimeMicrophoneReason = "manual",
  ): void {
    this.m_session.setMuted(muted, reason);
  }

  forceStop(): boolean {
    return this.m_session.forceStop();
  }

  setPushToTalkActive(
    active: boolean,
    opts?: { readonly autoRelease?: boolean },
  ): void {
    if (this.m_isPushToTalkActive === active) return;
    const autoRelease = active
      ? opts?.autoRelease === true
      : this.m_isPushToTalkAutoRelease;
    this.m_isPushToTalkActive = active;
    this.m_isPushToTalkAutoRelease = active ? autoRelease : false;
    if (this.usePushToTalk) {
      if (active) {
        this.m_log.note(
          "ptt.engaged",
          `auto_release=${autoRelease}`,
          { auto_release: autoRelease },
        );
        this.setMuted(false, "push_to_talk");
      } else {
        this.m_log.note(
          "ptt.released",
          `auto_release=${autoRelease}, recording_tail_ms=${K_PTT_RELEASE_TAIL_MS}`,
          {
            auto_release: autoRelease,
            recording_tail_ms: K_PTT_RELEASE_TAIL_MS,
          },
        );
        this.setMuted(true, "push_to_talk");
      }
    }
    // The indicator and microphone gate now follow the key in the same call.
    this.onPushToTalkActiveChanged.emit(active);
  }

  /**
   * `persist: false` changes the mode for THIS session only, leaving the stored
   * `Campfire.AI.ConstantListeningEnabled` preference exactly as it was. The
   * phone shell needs it: it forces constant listening on while its Spark panel
   * is open (there is no Space key to hold for push-to-talk) and restores the
   * user's value on close — and a persisting write would rewrite that value the
   * instant the panel opened, so a reload or a closed tab mid-session would
   * make the override permanent. Every other caller persists, as before.
   *
   * The mute state is applied unconditionally rather than inside the
   * unchanged-value early return: `AIRealtimeSessionModel` starts MUTED, so a
   * caller re-asserting a mode the model already believes it is in still needs
   * the microphone brought into line with it.
   */
  setConstantListeningEnabled(enabled: boolean, persist = true): void {
    const changed = this.m_isConstantListening !== enabled;
    this.m_isConstantListening = enabled;
    if (persist && changed) writePref(PREF_CONSTANT_LISTENING, enabled);
    this.setMuted(!enabled, "constant_listening");
    if (changed) this.onConstantListeningChanged.emit(enabled);
  }

  /**
   * Tell the model whether the microphone is being driven by hand. See
   * `m_isVoiceInputManual`; the one consequence is that `Listen_User` is
   * refused for as long as it is set.
   */
  setVoiceInputManual(manual: boolean): void {
    this.m_isVoiceInputManual = manual;
  }

  /** `Listen_User` tool: open the mic for one voice reply. */
  requestListenUser(): boolean {
    // The learner is arming the mic themselves — the `verify` skill (guide
    // scoped) is told to reach for this tool for a spoken reply, and on a phone
    // during a run that would open the mic behind them.
    if (this.m_isVoiceInputManual) return false;
    if (this.m_isConstantListening) return false;
    this.setPushToTalkActive(true, { autoRelease: true });
    return true;
  }

  setDebugMode(enabled: boolean): void {
    if (this.m_isDebugMode === enabled) return;
    this.m_isDebugMode = enabled;
    writePref(PREF_DEBUG_MODE, enabled);
    this.onDebugModeChanged.emit(enabled);
  }

  setShowPanelOnlyForResponsesAndCitations(enabled: boolean): void {
    if (this.m_minimalPanelMode === enabled) return;
    this.m_minimalPanelMode = enabled;
    writePref(PREF_MINIMAL_PANEL, enabled);
    this.onMinimalPanelModeChanged.emit(enabled);
  }

  private emitActivityState(): void {
    this.onActivityStateChanged.emit(this.isActivityActive);
  }

  // -- Progressive-disclosure skills & roles ---------------------------
  // Port of the Unity `AIInterfaceModel` skill region: available/loaded skill
  // state, the active role id, layered tool gating, and the two prompt
  // sections (whose exact copy lives in `skillPromptSections.ts`).

  get availableSkills(): ReadonlyArray<AIRealtimeSkillDefinition> {
    return this.m_availableSkills;
  }

  /**
   * The whole catalog, INCLUDING skills the account has switched off. For the
   * debug panel's settings tab only — it needs the disabled ones to render the
   * switch that brings them back. Anything the agent can observe reads
   * {@link availableSkills} instead.
   */
  get allSkills(): ReadonlyArray<AIRealtimeSkillDefinition> {
    return this.m_allSkills;
  }

  /** Whether the account's settings leave this skill available to the agent. */
  isSkillEnabled(skillId: string): boolean {
    return this.m_appSettings.isSkillEnabled(skillId);
  }

  /**
   * Publishes a skill's account-wide on/off state. Resolves false if the write
   * failed (see `IAppSettingsModel.lastError`); the local filter is applied
   * optimistically either way, through the settings model's change event.
   */
  setSkillEnabled(skillId: string, enabled: boolean): Promise<boolean> {
    return this.m_appSettings.setSkillEnabled(skillId, enabled);
  }

  get loadedSkills(): ReadonlyArray<AIRealtimeSkillDefinition> {
    return this.m_loadedSkills;
  }

  get availableSkillsForActiveRole(): ReadonlyArray<AIRealtimeSkillDefinition> {
    if (!this.m_activeRoleId) return this.m_availableSkills;
    return this.skillsForRole(this.m_activeRoleId);
  }

  /**
   * The account-enabled skills in a role's scope — the same list
   * {@link availableSkillsForActiveRole} gives for the live role, asked about
   * ANY role, which is what the debug panel's per-role section renders.
   */
  skillsForRole(roleId: string | null): ReadonlyArray<AIRealtimeSkillDefinition> {
    return this.m_availableSkills.filter((skill) => this.isSkillInRoleScope(skill, roleId));
  }

  /**
   * **Whether a skill is in a role's scope — the one predicate every surface
   * asks.** The account's stored override wins; absent, the skill's own
   * `roles:` decides, exactly as before this existed.
   *
   * There was no such choke point before: `appliesToRole` was called directly
   * from five places (the prompt index, this getter, `Load_Skill`'s executor
   * check, the loaded-skill prune, and the debug panel twice) and each was the
   * whole answer. An override that ADDS a role is what forces them together —
   * any site still asking the definition alone would quietly undo it, and the
   * failure would look like "the toggle does nothing" on one surface while
   * working on the others.
   *
   * Note it does NOT fold in the account-wide skill gate. That gate produces
   * `m_availableSkills`, so every caller here is already working from the
   * enabled set; folding it in again would be a second answer to a question
   * `applySkillGate` has already settled.
   */
  isSkillInRoleScope(skill: AIRealtimeSkillDefinition, roleId: string | null): boolean {
    const override = roleId ? this.m_appSettings.getRoleSkillOverride(roleId, skill.id) : null;
    return override ?? skill.appliesToRole(roleId);
  }

  /** Whether the account has stored a scope decision for this pair at all. */
  isSkillRoleScopeOverridden(skill: AIRealtimeSkillDefinition, roleId: string | null): boolean {
    if (!roleId) return false;
    return this.m_appSettings.getRoleSkillOverride(roleId, skill.id) !== null;
  }

  /**
   * Puts a skill in or out of a role's scope for the whole account.
   *
   * The prune-to-default policy lives HERE rather than in the settings model,
   * which cannot see the catalog: a value equal to what the skill's file already
   * declares clears the override instead of storing it, so a later change to a
   * shipped skill's `roles:` is not overruled by a stale document. Same rule
   * `setRoleModel` follows for the session-model defaults.
   *
   * Resolves false for an unknown skill, or if the write failed.
   */
  setSkillInRoleScope(skillId: string, roleId: string, inScope: boolean): Promise<boolean> {
    const role = roleId?.trim().toLowerCase();
    if (!role) return Promise.resolve(false);
    const target = skillId?.trim().toLowerCase();
    const skill = target
      ? (this.m_allSkills.find((candidate) => candidate.id.toLowerCase() === target) ?? null)
      : null;
    if (!skill) return Promise.resolve(false);

    const declared = skill.appliesToRole(role);
    return this.m_appSettings.setRoleSkillOverride(
      role,
      skill.id,
      inScope === declared ? null : inScope,
    );
  }

  /**
   * The roles a skill is EFFECTIVELY reachable from, for the "switch to one of
   * these roles first" refusal `Load_Skill` prints. Reads the enabled role
   * catalog and this predicate, so it names what is true now rather than what
   * the file says — with an override in play those differ, and the message is
   * the agent's only instruction about where to go.
   */
  getEffectiveSkillRoles(skill: AIRealtimeSkillDefinition): ReadonlyArray<string> {
    return this.m_availableRoles
      .filter((role) => this.isSkillInRoleScope(skill, role.id))
      .map((role) => role.id);
  }

  get activeRoleId(): string | null {
    return this.m_activeRoleId;
  }

  setActiveRoleId(roleId: string | null): void {
    const normalized = roleId?.trim() ? roleId.trim().toLowerCase() : null;
    if ((this.m_activeRoleId ?? null) === normalized) return;

    this.m_activeRoleId = normalized;
    this.pruneLoadedSkillsForActiveRole();
    // Re-baseline, or the next settings event would read the new role's scoped
    // set as an override change and refresh the session for nothing.
    this.m_activeRoleScopeSignature = this.activeRoleScopeSignature();
    this.onSkillsChanged.emit();
  }

  /**
   * The role definitions the account leaves available (id + display name +
   * description) — what the "@" role picker offers and what `switch_role` lists.
   */
  get availableRoles(): ReadonlyArray<AIRealtimeRoleDefinition> {
    return this.m_availableRoles;
  }

  /**
   * The whole catalog, INCLUDING roles the account has switched off — the same
   * split as {@link allSkills}, and for the same reason: the settings tab has to
   * list a disabled role to offer the switch that brings it back.
   */
  get allRoles(): ReadonlyArray<AIRealtimeRoleDefinition> {
    return this.m_roleDefinitions;
  }

  /**
   * Whether the account leaves this role available.
   *
   * The DEFAULT role always answers true, whatever the document says. It is the
   * fallback every unresolved role lands on — a session starting, a project
   * opening or closing, a role withdrawn while active — so a build that let it be
   * switched off would have no persona to fall back TO. Enforced on read as well
   * as on write so a hand-edited document cannot get past it.
   */
  isRoleEnabled(roleId: string): boolean {
    if (isDefaultRoleId(roleId)) return true;
    return this.m_appSettings.isRoleEnabled(roleId);
  }

  /** Whether the role may be switched off at all — false for the default role. */
  canDisableRole(roleId: string): boolean {
    return !isDefaultRoleId(roleId);
  }

  /**
   * The role that should actually become active for a requested id: the id
   * itself, or the default role when it is blank or the account has withdrawn it.
   *
   * A withdrawn role can still be ASKED for — the agent's `switch_role`, a stale
   * "@" pick, or a workflow run reaching for the Guide — and the caller has to end
   * up agreeing with the session about which role is live, so this is a query
   * rather than a silent coercion inside {@link setActiveRoleId}.
   */
  resolveEnabledRoleId(roleId: string | null): string {
    const id = roleId?.trim() ? roleId.trim().toLowerCase() : DEFAULT_ROLE_ID;
    return this.isRoleEnabled(id) ? id : DEFAULT_ROLE_ID;
  }

  /**
   * Publishes a role's account-wide on/off state. Same contract as
   * {@link setSkillEnabled}, plus one refusal: the default role cannot be
   * disabled (see {@link isRoleEnabled}).
   */
  setRoleEnabled(roleId: string, enabled: boolean): Promise<boolean> {
    if (!enabled && !this.canDisableRole(roleId)) return Promise.resolve(false);
    return this.m_appSettings.setRoleEnabled(roleId, enabled);
  }

  /** The active role's full definition (for the input role token), or null for the default role. */
  get activeRole(): AIRealtimeRoleDefinition | null {
    return this.findRoleById(this.m_activeRoleId);
  }

  /** The orchestrator registers its role-switch handler here (see {@link AIRoleController}). */
  attachRoleController(controller: AIRoleController): void {
    this.m_roleController = controller;
  }

  /**
   * The Spark panel registers its conversation controls here, and gets back the
   * release for THIS registration — see {@link AITranscriptController} for why
   * a disposer rather than a nullable setter.
   */
  attachTranscriptController(controller: AITranscriptController): () => void {
    this.m_transcriptController = controller;
    return () => {
      // Only ours. A panel that has already been replaced must not clear its
      // successor's registration on the way out.
      if (this.m_transcriptController === controller) this.m_transcriptController = null;
    };
  }

  /** What the conversation is doing, or null when no Spark panel is mounted. */
  get transcriptState(): AITranscriptState | null {
    return this.m_transcriptController?.getTranscriptState() ?? null;
  }

  /**
   * `Toggle_Transcript` — show or hide the conversation. Null when no Spark
   * panel is mounted to command, which the executor reports as its own refusal.
   */
  setTranscriptExpanded(expanded: boolean): AITranscriptState | null {
    return this.m_transcriptController?.setTranscriptExpanded(expanded) ?? null;
  }

  /**
   * User-driven role switch from the "@" picker. Routes through the orchestrator controller
   * (persona + tool-gating + session refresh); falls back to a local id set if none is attached.
   */
  switchAgentRole(roleId: string): void {
    if (this.m_roleController) this.m_roleController.switchRole(roleId);
    else this.setActiveRoleId(roleId);
  }

  isSkillLoaded(skillId: string): boolean {
    if (!skillId?.trim()) return false;
    const target = skillId.trim().toLowerCase();
    return this.m_loadedSkillIds.some((id) => id.toLowerCase() === target);
  }

  /**
   * Marks a skill loaded (validated against the available catalog). Returns the loaded definition
   * or null. Refreshes the live session so the full guidance lands in the next prompt — unless
   * `refreshSession` is false, for a caller about to push its own `session.update` anyway (a role
   * switch), which would otherwise send the ~55 KB payload twice.
   */
  tryLoadSkill(skillId: string, refreshSession = true): AIRealtimeSkillDefinition | null {
    const skill = this.findAvailableSkillById(skillId);
    if (!skill) return null;
    if (this.isSkillLoaded(skill.id)) return skill;

    this.m_loadedSkillIds.push(skill.id);
    this.refreshLoadedSkillsFromIds();
    this.onSkillsChanged.emit();
    if (refreshSession) this.m_session.refreshSessionContext();
    return skill;
  }

  unloadSkill(skillId: string): boolean {
    if (!skillId?.trim()) return false;
    const target = skillId.trim().toLowerCase();
    const before = this.m_loadedSkillIds.length;
    for (let i = this.m_loadedSkillIds.length - 1; i >= 0; i--) {
      if (this.m_loadedSkillIds[i].toLowerCase() === target) this.m_loadedSkillIds.splice(i, 1);
    }
    if (this.m_loadedSkillIds.length === before) return false;

    this.refreshLoadedSkillsFromIds();
    this.onSkillsChanged.emit();
    this.m_session.refreshSessionContext();
    return true;
  }

  /**
   * Loads the base prompt, personalities, skills and roles (idempotent). Awaited
   * before the first session connect.
   *
   * The settings document and the published prompts are read FIRST, not
   * concurrently with the catalogs, because both have to be in hand before a
   * catalog is built: the skill/role gates decide what the agent is told exists,
   * and the published prompts decide what each definition SAYS — and
   * `AgentPromptSource.getPromptText` is synchronous, so a catalog that ran first
   * would build from shipped text and need a second full pass once the documents
   * landed.
   *
   * Neither can fail the boot. Both models resolve either way and fall back to
   * "everything enabled, nothing published" — the same answer as no document.
   */
  async ensureContentLoadedAsync(): Promise<void> {
    await Promise.all([
      this.m_appSettings.ensureLoadedAsync(),
      this.m_prompts.ensureLoadedAsync(),
    ]);
    await Promise.all([
      ensureBasePromptLoadedAsync(this.m_prompts),
      ensurePersonalitiesLoadedAsync(this.m_prompts),
      this.refreshSkillsAsync(),
      this.refreshRolesAsync(),
    ]);
  }

  /** The deployment's published prompts, for the debug panel's content tabs. */
  get prompts(): IAIPromptStoreModel {
    return this.m_prompts;
  }

  /**
   * Re-reads BOTH catalogs from source and pushes the result to the live session.
   *
   * This is the seam a developer prompt override needs, and it is separate from
   * `ensureContentLoadedAsync` for a reason: that method is idempotent by design
   * and becomes a permanent no-op after the first call, so it can never re-read a
   * changed file.
   *
   * The trailing `refreshSessionContext()` is the load-bearing part. The roles
   * path already refreshes on its own, but that fires MID-reload — carrying a new
   * persona alongside the old skill bodies — and the skills path deliberately
   * does not refresh at all (its only callers are this method and the pre-connect
   * boot, where there is no session yet). One unconditional send at the end is
   * what actually puts the edited prompt on the wire.
   *
   * Ids come from `index.json`, which is never overridable, so the active role
   * and every loaded skill re-bind by id across the reload: `m_activeRoleId`
   * still resolves and `refreshLoadedSkillsFromIds()` re-points the loaded ids at
   * the new bodies.
   */
  async reloadContentAsync(): Promise<void> {
    if (this.m_contentReloadTask) return this.m_contentReloadTask;
    return this.requestContentReloadAsync();
  }

  /**
   * Schedules an effective-content change. A second model event while a reload
   * is in flight must get one pass AFTER that reload; simply returning the
   * current promise can coalesce away a rapid cross-tab edit. Direct UI callers
   * use {@link reloadContentAsync} to join the event-triggered pass without
   * queuing a duplicate one.
   */
  private requestContentReloadAsync(): Promise<void> {
    if (this.m_contentReloadTask) {
      this.m_contentReloadQueued = true;
      return this.m_contentReloadTask;
    }

    const reload = this.reloadContentUntilSettledAsync();
    const tracked = reload.finally(() => {
      if (this.m_contentReloadTask === tracked) this.m_contentReloadTask = null;
    });
    this.m_contentReloadTask = tracked;
    return tracked;
  }

  private async reloadContentUntilSettledAsync(): Promise<void> {
    let succeeded = false;
    do {
      this.m_contentReloadQueued = false;
      succeeded = await this.reloadContentInternalAsync();
    } while (this.m_contentReloadQueued);

    if (!succeeded) {
      throw new Error("One or more prompt catalogs could not be reloaded; their last successful content remains active.");
    }
  }

  private async reloadContentInternalAsync(): Promise<boolean> {
    // `force` on all four: every one of these caches its first load for the
    // session, which is exactly what a publish or reset has to defeat.
    const [rolesLoaded, skillsLoaded] = await Promise.all([
      this.refreshRolesAsync(true),
      this.refreshSkillsAsync(true),
      ensureBasePromptLoadedAsync(this.m_prompts, true),
      ensurePersonalitiesLoadedAsync(this.m_prompts, true),
    ]);
    this.m_session.refreshSessionContext();
    return rolesLoaded && skillsLoaded;
  }

  // -- The core tool set -----------------------------------------------
  // What every role keeps regardless of its `tools:` list. The build ships
  // `K_CORE_ALWAYS_ALLOWED_TOOL_IDS`; the deployment's settings document can add
  // an id or withdraw one, and THIS is where the two are composed. Every caller
  // asks here — reading the constant directly answers a different question (see
  // its note).

  /**
   * Whether a tool is core RIGHT NOW: the deployment's stored decision if it has
   * one, otherwise what this build ships. Ids are compared lowercased, like the
   * tools model's own filter.
   */
  isToolCore(toolId: string): boolean {
    const id = toolId?.trim().toLowerCase();
    if (!id) return false;
    return this.m_appSettings.getCoreToolOverride(id) ?? K_CORE_ALWAYS_ALLOWED_TOOL_IDS.has(id);
  }

  /** Whether the deployment has stored a decision for this tool at all. */
  isToolCoreOverridden(toolId: string): boolean {
    const id = toolId?.trim().toLowerCase();
    if (!id) return false;
    return this.m_appSettings.getCoreToolOverride(id) !== null;
  }

  /**
   * The EFFECTIVE core set, lowercased — the shipped ids minus what the
   * deployment has withdrawn, plus what it has added. This is what the role gate
   * layers a role's declared list on top of, and what the debug panel counts.
   */
  get coreToolIds(): ReadonlySet<string> {
    const ids = new Set<string>();
    for (const id of K_CORE_ALWAYS_ALLOWED_TOOL_IDS) {
      if (this.m_appSettings.getCoreToolOverride(id) !== false) ids.add(id);
    }
    // Added ids can name a tool this client never registered, exactly like a
    // shipped entry for an unported tool — an allow-list entry for a tool that
    // does not exist is inert, so there is nothing to validate here.
    for (const [id, isCore] of Object.entries(this.m_appSettings.coreToolOverrides)) {
      if (isCore) ids.add(id);
    }
    return ids;
  }

  /**
   * Publishes whether a tool is core, for the whole deployment.
   *
   * The prune-to-shipped policy lives here rather than in the settings model,
   * which cannot see `K_CORE_ALWAYS_ALLOWED_TOOL_IDS`: a value that agrees with
   * the build clears the entry instead of storing it, so a later build that adds
   * or drops a core id is not overruled by a stale document. Same rule
   * {@link setSkillInRoleScope} and `setRoleModel` follow.
   *
   * Resolves false for a blank id, or if the write failed.
   */
  setToolCore(toolId: string, isCore: boolean): Promise<boolean> {
    const id = toolId?.trim().toLowerCase();
    if (!id) return Promise.resolve(false);
    const shipped = K_CORE_ALWAYS_ALLOWED_TOOL_IDS.has(id);
    return this.m_appSettings.setCoreToolOverride(id, isCore === shipped ? null : isCore);
  }

  /**
   * Allowed tool ids for the active role + loaded skills, or null when the role declares no
   * restriction (expose all enabled tools — backward compatible). Ids are lowercased; the tools
   * model filters case-insensitively.
   */
  getAllowedToolIdsForActiveRole(): ReadonlySet<string> | null {
    const role = this.findRoleById(this.m_activeRoleId);
    const roleTools = role?.tools;
    if (!roleTools || roleTools.length === 0) return null;

    const allowed = new Set<string>(this.coreToolIds);
    for (const tool of roleTools) {
      if (tool?.trim()) allowed.add(tool.trim().toLowerCase());
    }

    // Loaded skills unlock their own tools on top of the role's base set.
    for (const skill of this.m_loadedSkills) {
      for (const tool of skill.tools) {
        if (tool?.trim()) allowed.add(tool.trim().toLowerCase());
      }
    }

    return allowed;
  }

  buildAvailableSkillsIndexSection(): string | null {
    return buildAvailableSkillsIndexSection(
      this.m_availableSkills,
      (skill) => this.isSkillInRoleScope(skill, this.m_activeRoleId),
      (id) => this.isSkillLoaded(id),
    );
  }

  buildLoadedSkillsPromptSection(): string | null {
    return buildLoadedSkillsPromptSection(this.m_loadedSkills);
  }

  private refreshSkillsAsync(force = false): Promise<boolean> {
    // A FORCED refresh must not resolve to an in-flight load: that load is
    // already reading the old text, so an upload landing during boot would
    // silently resolve to the stock catalog. Chain a fresh pass behind it.
    if (this.m_skillsLoadTask) {
      return force
        ? this.m_skillsLoadTask.then(() => this.refreshSkillsAsync(true))
        : this.m_skillsLoadTask;
    }
    if (this.m_hasLoadedSkillCatalog && !force) return Promise.resolve(true);

    this.m_skillsLoadTask = this.refreshSkillsInternalAsync().finally(() => {
      this.m_skillsLoadTask = null;
    });
    return this.m_skillsLoadTask;
  }

  private async refreshSkillsInternalAsync(): Promise<boolean> {
    let loaded = false;
    this.onSkillsChanged.emit();
    try {
      const availableSkills = await loadAvailableSkillsAsync(this.m_prompts);
      // NULL is "could not read the catalog", not "there are no skills". Keeping
      // the previous catalog matters most on a RELOAD: replacing it with an empty
      // one makes `refreshLoadedSkillsFromIds()` splice every loaded skill id out
      // of `m_loadedSkillIds`, which a later successful reload cannot undo.
      if (availableSkills === null) {
        console.warn("[AIInterfaceModel] Skill catalog unreadable; keeping the previous catalog.");
        this.m_hasLoadedSkillCatalog = false;
      } else {
        this.m_allSkills.length = 0;
        this.m_allSkills.push(...availableSkills);
        this.applySkillGate();
        // Only a real read counts as "loaded", so a failure at boot is retried by
        // the next `ensureContentLoadedAsync` instead of latching an empty
        // catalog for the session.
        this.m_hasLoadedSkillCatalog = true;
        loaded = true;
      }
    } catch (e) {
      console.warn("[AIInterfaceModel] Failed to load realtime skills:", e);
    } finally {
      this.refreshLoadedSkillsFromIds();
      this.recomputeContentUsesOverrides();
      this.onSkillsChanged.emit();
    }
    return loaded;
  }

  private refreshRolesAsync(force = false): Promise<boolean> {
    // See the note on `refreshSkillsAsync`.
    if (this.m_rolesLoadTask) {
      return force
        ? this.m_rolesLoadTask.then(() => this.refreshRolesAsync(true))
        : this.m_rolesLoadTask;
    }
    if (this.m_hasLoadedRoleCatalog && !force) return Promise.resolve(true);

    this.m_rolesLoadTask = this.refreshRolesInternalAsync().finally(() => {
      this.m_rolesLoadTask = null;
    });
    return this.m_rolesLoadTask;
  }

  private async refreshRolesInternalAsync(): Promise<boolean> {
    try {
      const roles = await loadAvailableRolesAsync(this.m_prompts);
      // NULL is "could not read the catalog", not "there are no roles" — and the
      // difference is a safety one, not a nicety. Overwriting a live catalog with
      // an empty list empties `s_rolePersonas` (so `{role}` substitutes to "")
      // AND makes `getAllowedToolIdsForActiveRole()` return null, which the
      // registry reads as NO RESTRICTION — i.e. a transient dev-server hiccup
      // during a reload would hand the agent every enabled tool. Keep what we
      // have, stay un-loaded, and do not touch the wire.
      if (roles === null) {
        console.warn("[AIInterfaceModel] Role catalog unreadable; keeping the previous catalog.");
        this.m_hasLoadedRoleCatalog = false;
        return false;
      }

      this.m_roleDefinitions.length = 0;
      this.m_roleDefinitions.push(...roles);
      // The GATED subset is what reaches `setRoleDefinitions`, so the persona map
      // and `switch_role` are built from the roles the account actually offers.
      this.applyRoleGate();
      this.m_hasLoadedRoleCatalog = true;
      this.recomputeContentUsesOverrides();

      // The persona (and per-role tool gating) is part of the session; refresh so it takes effect.
      this.m_session.refreshSessionContext();
      return true;
    } catch (e) {
      // Unreachable via the loader (it returns null rather than throwing), so
      // this is a genuine programming error — keep the previous catalog for the
      // same reason as above rather than degrading to the bootstrap ids.
      console.warn("[AIInterfaceModel] Failed to load realtime roles:", e);
      this.m_hasLoadedRoleCatalog = false;
      return false;
    }
  }

  /**
   * Recomputes the enabled role subset and republishes it to `agentPrompts`,
   * moving the session off a role that has just been withdrawn.
   *
   * Returns whether the subset CHANGED, for the same reason {@link applySkillGate}
   * does: the settings model raises one payload-free event for the whole shared
   * document, and unrelated dev-settings toggles must not push a fresh `session.update`.
   */
  private applyRoleGate(): boolean {
    const enabled = this.m_roleDefinitions.filter((role) => this.isRoleEnabled(role.id));
    const isUnchanged =
      enabled.length === this.m_availableRoles.length &&
      enabled.every((role, index) => role === this.m_availableRoles[index]);
    if (isUnchanged) return false;

    this.m_availableRoles.length = 0;
    this.m_availableRoles.push(...enabled);
    // The gated list, not the catalog: this is what `switch_role`'s enum and
    // description, `tryResolveRoleId`, and the persona map are all built from, so
    // one call withdraws a role from every agent-facing path at once. An empty
    // list means the catalog is empty (the gate cannot empty a catalog that has
    // the default role in it) and keeps the existing bootstrap fallback.
    setRoleDefinitions(this.m_availableRoles);
    this.leaveDisabledActiveRole();
    return true;
  }

  /**
   * Moves the session to the default role when the active one is no longer
   * available.
   *
   * Without this, withdrawing the role someone is currently in leaves the session
   * with a persona that `setRoleDefinitions` has just dropped: `{role}` would
   * substitute to empty and `switch_role` would not list the role the agent is
   * supposedly in, so it could not even switch away.
   */
  private leaveDisabledActiveRole(): void {
    const active = this.m_activeRoleId;
    if (!active || this.isRoleEnabled(active)) return;

    if (this.m_roleController) this.m_roleController.switchRole(DEFAULT_ROLE_ID);
    else this.setActiveRoleId(DEFAULT_ROLE_ID);
  }

  private recomputeContentUsesOverrides(): void {
    this.m_contentUsesPublished =
      this.m_roleDefinitions.some((role) => role.isPublished) ||
      // The WHOLE catalog, not the enabled subset: this flag decides whether a
      // published file needs a catalog re-read, and one on a disabled skill still
      // has to be picked up (and dropped) like any other — the skill may be
      // re-enabled at any moment.
      this.m_allSkills.some((skill) => skill.isPublished);
  }

  /**
   * Recomputes the enabled subset from the catalog and the account's gate,
   * unloading any loaded skill that just became unavailable.
   *
   * Returns whether the subset actually CHANGED. The settings model raises one
   * payload-free event for the whole shared document, most of which is dev-panel
   * switches, so this is what keeps unrelated dev-settings toggles from pushing a fresh
   * `session.update` at the live agent.
   */
  private applySkillGate(): boolean {
    const enabled = this.m_allSkills.filter((skill) => this.m_appSettings.isSkillEnabled(skill.id));
    const isUnchanged =
      enabled.length === this.m_availableSkills.length &&
      enabled.every((skill, index) => skill === this.m_availableSkills[index]);
    if (isUnchanged) return false;

    this.m_availableSkills.length = 0;
    this.m_availableSkills.push(...enabled);
    // A skill that was LOADED and has just been disabled no longer resolves
    // here, so this both drops it from the prompt and splices its id — the same
    // mechanism a removed manifest entry goes through.
    this.refreshLoadedSkillsFromIds();
    return true;
  }

  private pruneLoadedSkillsForActiveRole(): void {
    if (!this.m_activeRoleId || this.m_loadedSkillIds.length === 0) return;

    let removed = false;
    for (let i = this.m_loadedSkillIds.length - 1; i >= 0; i--) {
      const skill = this.findAvailableSkillById(this.m_loadedSkillIds[i]);
      if (skill && !this.isSkillInRoleScope(skill, this.m_activeRoleId)) {
        this.m_loadedSkillIds.splice(i, 1);
        removed = true;
      }
    }

    if (removed) this.refreshLoadedSkillsFromIds();
  }

  /**
   * Reacts to a per-role scope override changing under the LIVE role: drops a
   * loaded skill that has just left its scope, and reports whether the active
   * role's scoped set moved at all.
   *
   * Signature-based rather than array-identity like {@link applySkillGate},
   * because the scoped set is derived on read and has no cached array to
   * compare against — and the answer has to be "did anything the agent can
   * observe change", since the settings model raises ONE payload-free event for
   * a document that is mostly dev-panel switches.
   */
  private applyRoleSkillScope(): boolean {
    const signature = this.activeRoleScopeSignature();
    if (signature === this.m_activeRoleScopeSignature) return false;
    this.m_activeRoleScopeSignature = signature;
    this.pruneLoadedSkillsForActiveRole();
    return true;
  }

  private activeRoleScopeSignature(): string {
    return this.availableSkillsForActiveRole.map((skill) => skill.id).join("|");
  }

  /**
   * Reacts to the deployment's core tool set changing, and reports whether it
   * actually moved.
   *
   * Diffs the EFFECTIVE set rather than the stored overrides, for the same
   * reason {@link applyRoleSkillScope} does: an entry pruned back to absence
   * because it agreed with the build changes the document and nothing the agent
   * can observe, and a settings event carrying only dev-panel switches must not
   * push a fresh `session.update` at a live agent.
   */
  private applyCoreToolSet(): boolean {
    const signature = toolIdSignature(this.coreToolIds);
    if (signature === this.m_coreToolSignature) return false;
    this.m_coreToolSignature = signature;
    return true;
  }

  private refreshLoadedSkillsFromIds(): void {
    const loaded: AIRealtimeSkillDefinition[] = [];
    for (let i = this.m_loadedSkillIds.length - 1; i >= 0; i--) {
      const skill = this.findAvailableSkillById(this.m_loadedSkillIds[i]);
      if (!skill) {
        this.m_loadedSkillIds.splice(i, 1);
        continue;
      }
      loaded.unshift(skill);
    }
    this.m_loadedSkills = loaded;
  }

  private findAvailableSkillById(skillId: string): AIRealtimeSkillDefinition | null {
    if (!skillId?.trim()) return null;
    const target = skillId.trim().toLowerCase();
    return this.m_availableSkills.find((skill) => skill.id.toLowerCase() === target) ?? null;
  }

  private findRoleById(roleId: string | null): AIRealtimeRoleDefinition | null {
    if (!roleId?.trim()) return null;
    const target = roleId.trim().toLowerCase();
    return this.m_roleDefinitions.find((role) => role.id.toLowerCase() === target) ?? null;
  }
}

export const AI_INTERFACE_MODEL = Symbol.for("Campfire.AIInterfaceModel");
